import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LogOut, 
  ChevronDown,
  ChevronRight,
  Users
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useDashboard } from '../../context/DashboardContext';
import { toast } from 'sonner';
import { MODULE_CATEGORIES } from '../../config/moduleRegistry';

const SidebarNavigation = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const { 
    enabledModules, 
    userRole, 
    currentOrganization,
    dashboardConfig 
  } = useDashboard();

  // Collapsed categories state
  const [collapsedCategories, setCollapsedCategories] = useState({});

  const handleLogout = async () => {
    const result = await signOut();
    if (result.success) {
      toast.success('Logged out successfully');
      navigate('/');
    }
  };

  const toggleCategory = (category) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Group modules by category
  const modulesByCategory = enabledModules.reduce((acc, module) => {
    const cat = module.category || MODULE_CATEGORIES.CORE;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(module);
    return acc;
  }, {});

  // Category order
  const categoryOrder = [
    MODULE_CATEGORIES.CORE,
    MODULE_CATEGORIES.MANAGEMENT,
    MODULE_CATEGORIES.ENGAGEMENT,
    MODULE_CATEGORIES.OPERATIONS
  ];

  const isActiveRoute = (modulePath) => {
    if (modulePath === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(modulePath);
  };

  // Get role display name
  const getRoleDisplayName = (role) => {
    return role?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'User';
  };

  return (
    <>
      {/* Mobile Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>
      
      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full w-64 bg-card border-r border-border z-50 transform transition-transform lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar-navigation"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-border">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-heading text-xl font-bold">
                <span className="text-primary">MICHEZO</span>
                <span className="text-secondary">NET</span>
              </span>
            </Link>
            {/* Role Badge */}
            <div className="mt-3 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-body inline-block">
              {dashboardConfig?.title?.replace(' Dashboard', '') || getRoleDisplayName(userRole)}
            </div>
          </div>
          
          {/* Organization Switcher (if user has multiple) */}
          {currentOrganization && (
            <div className="px-4 py-3 border-b border-border">
              <p className="text-white/40 text-xs font-body uppercase tracking-wide mb-2">Organization</p>
              <div className="flex items-center gap-2 p-2 rounded-lg bg-accent/50">
                <div className="w-8 h-8 rounded bg-primary/20 flex items-center justify-center">
                  <Users size={14} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-body truncate">{currentOrganization.name}</p>
                  <p className="text-white/40 text-xs">{currentOrganization.type}</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-4 overflow-y-auto">
            {categoryOrder.map(category => {
              const modules = modulesByCategory[category];
              if (!modules || modules.length === 0) return null;
              
              const isCollapsed = collapsedCategories[category];
              
              return (
                <div key={category}>
                  {/* Category Header */}
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full flex items-center justify-between px-2 py-1 text-white/40 text-xs font-body uppercase tracking-wide hover:text-white/60 transition-colors"
                  >
                    <span>{category}</span>
                    <motion.span
                      animate={{ rotate: isCollapsed ? 0 : 90 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronRight size={12} />
                    </motion.span>
                  </button>
                  
                  {/* Category Items */}
                  <AnimatePresence initial={false}>
                    {!isCollapsed && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-1 mt-2">
                          {modules.map(module => {
                            const isActive = isActiveRoute(module.route);
                            const IconComponent = module.icon;
                            
                            return (
                              <Link
                                key={module.id}
                                to={module.route}
                                onClick={() => setIsOpen(false)}
                                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg font-body text-sm transition-all ${
                                  isActive 
                                    ? 'bg-primary/10 text-primary' 
                                    : 'text-white/70 hover:bg-accent hover:text-white'
                                }`}
                                data-testid={`nav-${module.id}`}
                              >
                                <IconComponent size={18} />
                                <span>{module.name}</span>
                              </Link>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </nav>
          
          {/* User & Logout */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 mb-4 px-2">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <Users size={18} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-body text-sm text-white truncate">{user?.email || 'User'}</p>
                <p className="text-white/50 text-xs font-body">{getRoleDisplayName(userRole)}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-body text-sm text-white/70 hover:bg-secondary/10 hover:text-secondary transition-all"
              data-testid="logout-btn"
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default SidebarNavigation;
